import { useMemo } from 'react';
import WrapperDataGrid from '@modules/dataGrid/WrapperDataGrid';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';

import { IConfigComponent, CReading, CUnitFormatter, CReadingType } from '@ts/ui-models';
import {
  generateGlobalColumns,
  generateBasicColumn,
  generateDateColumn,
  generateEntityColumn,
  IDataGridConfig,
} from '@ts/storybook';
import { useAppSelector } from '@redux/store';
import dayjs from 'dayjs';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';

interface IReadingsProps {
  itemsFetchUrl?: string;
  filters?: any;
  configComponent?: IConfigComponent;
  inheritedProps?: any;
}

export const defaultConfigComponent: IConfigComponent = {
  componentId: 'readings',
  aclId: 'tsReading',
  entity: 'reading',
  fetchURL: '/api/v2/readings',
  crudURL: '/api/v2/reading',
};

const Readings = (props: IReadingsProps) => {
  const { configComponent = defaultConfigComponent, inheritedProps } = props;
  const config: IDataGridConfig = {
    columnVisibilityModel: { id: false },
    filterModel: {
      items: props.configComponent?.where
        ? []
        : [
            {
              id: 'minimumFilter',
              field: 'timestamp',
              value: dayjs().startOf('month').format('YYYY-MM-DDTHH:mm'),
              operator: 'after',
            },
          ],
    },
  };
  const readingTypeList = useAppSelector(state => state.global.readingTypesList);
  const unitsList = useAppSelector(state => state.global.unitsList);
  const unitFormatter = new CUnitFormatter(unitsList, readingTypeList);

  const getColumns = useMemo((): GridColDef<any>[] => {
    const mixProps = { ...props, configComponent };
    const globalCells = generateGlobalColumns(mixProps);
    return [
      globalCells.id(),
      //globalCells.name(),
      generateEntityColumn(
        { field: 'readingType', headerName: 'Reading Type', isFilterable: true },
        { tEntity: 'readingType', mainClass: CReadingType },
        { inheritedProps, generateSearchEntitySelector }
      ),
      generateDateColumn({ field: 'timestamp', headerName: 'Timestamp', isFilterable: true }, { field: 'timestamp' }),
      {
        field: 'data',
        headerName: 'Data',
        filterable: false,
        valueGetter: (value, row, column) => {
          const readingTypeId = row.readingTypeId;
          const tReading = new CReading(readingTypeId, [row], unitFormatter);
          return typeof tReading.getValueUnits === 'object' ? JSON.stringify(tReading.getValueUnits) : tReading.getValueUnits;
        },
        renderCell: (params: GridRenderCellParams) => {
          const readingTypeId = params.row.readingTypeId;
          const tReading = new CReading(readingTypeId, [params.row], unitFormatter);

          return tReading.getValueUnits;
        },
      },
      generateBasicColumn({ field: 'sourceElement', headerName: 'Source item' }, { field: 'sourceElement' }),
      generateBasicColumn({ field: 'sourceDevice', headerName: 'Source device' }, { field: 'sourceDevice' }),
      generateBasicColumn(
        { field: 'deliveryChain', headerName: 'Incoming data chain' },
        { tEntity: 'deliveryChain', field: 'name' }
      ),
    ];
  }, [props, configComponent]);

  const configComponentC = {
    ...defaultConfigComponent,
    fetchURL: props.itemsFetchUrl ?? configComponent.fetchURL,
    //filtersFetchData: props.filters,
  };

  return (
    <WrapperDataGrid
      configComponent={configComponentC}
      dataGridConfig={config}
      columns={getColumns}
      //Extra data
      dataGridMode="server"
      inheritedProps={inheritedProps}
      enabledFilters
      withoutActions
      withoutCreateEntity
    />
  );
};

export default Readings;
